/**
 * @module cercaliagl/layer/TrafficForecast
 */
/* eslint-disable quote-props */

import Servers from '../servers';

/**
 * @const
 * @type {string}
 */
const LayerId = 'TrafficForecastLayer';

/**
 * @const
 * @type {string}
 */
const SourceId = 'forecast';

/**
 * @typedef {Object} TrafficForecastOptions
 * @property {number} departuredayofweek Day of week
 * @property {number} departurefrommidnight Second of day. 0 to 86400
 */

/**
 * @classdesc
 */
class TrafficForecast {
  /**
   * @constructor
   * @param {TrafficForecastOptions} options Options
   */
  constructor(options) {
    /**
     * @private
     * @type {string}
     */
    this.id_ = LayerId;

    /**
     * @private
     * @type {import("maplibre-gl").Map}
     */
    this.map_ = null;

    /**
     * @private
     * @type {boolean}
     */
    this.visible_ = true;

    /**
     * @private
     * @type {number}
     */
    this.departuredayofweek_ = options.departuredayofweek;

    /**
     * @private
     * @type {number}
     */
    this.departurefrommidnight_ = options.departurefrommidnight;

    /**
     * @private
     * @type {import("maplibre-gl").Source}
     */
    this.source_ = {
      type: 'vector',
      tiles: this.replaceUrlsByParams_(Servers.forecast),
    };

    this.hoveredFeature_ = null;

    /**
     * @private
     * @type {Array.<Object.<string, any>>}
     */
    this.layers_ = [
      {
        'id': 'forecast_outline',
        'type': 'line',
        'source': 'forecast',
        'source-layer': 'roads',
        'minzoom': 9,
        'maxzoom': 19,
        'layout': {
          'line-cap': 'round',
          'line-join': 'round',
          'visibility': 'visible',
        },
        'paint': {
          'line-color': 'rgba(255, 255, 255, 1)',
          'line-width': [
            'case',
            ['==', ['get', 'frc'], 0], 6,
            ['==', ['get', 'frc'], 1], 6,
            ['==', ['get', 'frc'], 2], 5,
            ['==', ['get', 'frc'], 3], 4,
            ['==', ['get', 'frc'], 4], 4,
            ['==', ['get', 'frc'], 5], 4,
            0,
          ],
          'line-offset': [
            'case',
            ['==', ['get', 'frc'], 0], 3,
            ['==', ['get', 'frc'], 1], 3,
            ['==', ['get', 'frc'], 2], 2,
            ['==', ['get', 'frc'], 3], 2,
            ['==', ['get', 'frc'], 4], 2,
            ['==', ['get', 'frc'], 5], 2,
            ['==', ['get', 'frc'], 6], 2,
            ['==', ['get', 'frc'], 7], 2,
            ['==', ['get', 'frc'], 8], 2,
            0,
          ],
        },
      }, {
        'id': 'forecast28',
        'type': 'line',
        'source': 'forecast',
        'source-layer': 'roads',
        'minzoom': 9,
        'maxzoom': 19,
        'layout': {
          'line-cap': 'round',
          'line-join': 'round',
          'visibility': 'visible',
        },
        'paint': {
          'line-color': [
            'step', ['*', ['/', ['get', 'spfreeflow'], ['get', 'sptime']], 100],
            'rgba(255, 255, 255, 0)',
            1, 'rgba(129, 31, 31, 1)',
            30, 'rgba(242, 60, 50, 1)',
            65, 'rgba(255, 151, 77, 1)',
            80, 'rgba(99, 214, 104, 1)',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            4,
            [
              'case',
              ['==', ['get', 'frc'], 2], 3,
              ['==', ['get', 'frc'], 3], 2,
              ['==', ['get', 'frc'], 4], 2,
              ['==', ['get', 'frc'], 5], 2,
              ['==', ['get', 'frc'], 6], 1,
              ['==', ['get', 'frc'], 7], 1,
              ['==', ['get', 'frc'], 8], 1,
              0,
            ],
          ],
          'line-offset': [
            'case',
            ['==', ['get', 'frc'], 2], 2,
            ['==', ['get', 'frc'], 3], 2,
            ['==', ['get', 'frc'], 4], 2,
            ['==', ['get', 'frc'], 5], 2,
            ['==', ['get', 'frc'], 6], 0,
            ['==', ['get', 'frc'], 7], 0,
            ['==', ['get', 'frc'], 8], 0,
            0,
          ],
        },
      },
      {
        'id': 'forecast01',
        'type': 'line',
        'source': 'forecast',
        'source-layer': 'roads',
        'minzoom': 9,
        'maxzoom': 19,
        'layout': {
          'line-cap': 'round',
          'line-join': 'round',
          'visibility': 'visible',
        },
        'paint': {
          'line-color': [
            'step', ['*', ['/', ['get', 'spfreeflow'], ['get', 'sptime']], 100],
            'rgba(255, 255, 255, 0)',
            1, 'rgba(129, 31, 31, 1)',
            30, 'rgba(242, 60, 50, 1)',
            65, 'rgba(255, 151, 77, 1)',
            80, 'rgba(99, 214, 104, 1)',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            8,
            [
              'case',
              ['==', ['get', 'frc'], 1], 4,
              ['==', ['get', 'frc'], 0], 4,
              0,
            ],
          ],
          'line-offset': [
            'case',
            ['==', ['get', 'frc'], 0], 3,
            ['==', ['get', 'frc'], 1], 3,
            0,
          ],
        },
      },
    ];

    this.initialize_();
  }

  initialize_() {
    this.handleMouseOverFeature_ = this.handleMouseOverFeature_.bind(this);
    this.handleMouseLeaveFeature_ = this.handleMouseLeaveFeature_.bind(this);
  }

  /**
   * @private
   */
  addEventListeners_() {
    ['traffic_freeflow', 'traffic_nonfreeflow'].forEach((layerId) => {
      this.map_.on('mousemove', layerId, this.handleMouseOverFeature_);
      this.map_.on('mouseleave', layerId, this.handleMouseOverFeature_);
    });
  }

  /**
   * @private
   */
  removeEventListeners_() {
    ['traffic_freeflow', 'traffic_nonfreeflow'].forEach((layerId) => {
      this.map_.off('mousemove', layerId, this.handleMouseOverFeature_);
      this.map_.off('mouseleave', layerId, this.handleMouseOverFeature_);
    });
  }

  /**
   * @private
   * @param {Array.<string>} servers Servers
   * @return {Array.<string>}
   */
  replaceUrlsByParams_(servers) {
    return servers.map((server) => server
      .replace('{departuredayofweek}', this.departuredayofweek_)
      .replace('{departurefrommidnight}', this.departurefrommidnight_));
  }

  /**
   * @private
   * @param {Event} e Event
   */
  handleMouseOverFeature_(e) {
    if (e.features && e.features.length > 0) {
      if (this.hoveredFeature_) {
        this.map_.setFeatureState({
          id: this.hoveredFeature_.id,
          source: SourceId,
          sourceLayer: this.hoveredFeature_.layer['source-layer'],
        }, { hover: false });
      }
      [this.hoveredFeature_] = e.features;
      this.map_.setFeatureState({
        id: this.hoveredFeature_.id,
        source: SourceId,
        sourceLayer: this.hoveredFeature_.layer['source-layer'],
      }, { hover: true });
    }
  }

  /**
   * @private
   */
  handleMouseLeaveFeature_() {
    if (this.hoveredFeature_) {
      this.map_.setFeatureState({
        id: this.hoveredFeature_.id,
        source: SourceId,
        sourceLayer: this.hoveredFeature_.layer['source-layer'],
      }, { hover: false });
    }
    this.hoveredFeature_ = null;
  }

  /**
   * @return {string} Id.
   */
  getLayerId() {
    return this.id_;
  }

  /**
   * @param {import("../Map").default} map MAp
   */
  setMap(map) {
    if (map) {
      this.map_ = map.getMapboxglMap();
      this.map_.addSource(SourceId, this.source_);
      this.layers_.forEach((layer) => {
        this.map_.addLayer(layer);
      });
      this.addEventListeners_();
    } else {
      this.layers_.forEach((layer) => {
        this.map_.removeLayer(layer.id);
      });
      this.map_.removeSource(SourceId);
      this.removeEventListeners_();
    }
  }

  /**
   * @param {boolean} visible Visible
   */
  setVisible(visible) {
    this.visible_ = visible;
    this.map_.setLayoutProperty(this.id_, 'visibility', this.visible_ ? 'visible' : 'none');
  }

  /**
   * @param {number} departuredayofweek Departure day of week
   * @param {number} departurefrommidnight Departure day from midnight
   */
  updateParams(departuredayofweek, departurefrommidnight) {
    this.departuredayofweek_ = departuredayofweek;
    this.departurefrommidnight_ = departurefrommidnight;
    const servers = this.replaceUrlsByParams_(Servers.forecast);
    this.map_.getSource(SourceId).tiles = servers;
    this.source_.tiles = servers;
    this.map_.style.sourceCaches[SourceId].clearTiles();
    this.map_.style.sourceCaches[SourceId].update(this.map_.transform);
    this.map_.triggerRepaint();
  }

  /**
   * @return {Array.<Object.<string, any>>} Layers
   */
  getLayers() {
    return this.layers_;
  }
}

export default TrafficForecast;
